import { useCallback, useMemo } from "react";
import type { AxisSpec } from "../ChartSpec";
import { useDataViewerStore } from "../data-viewer-provider";
import { getLeftAxes } from "../spec/axes";

export interface LeftYAxisSelection {
    /** Left-side axes, in spec order — the candidates the metric picker switches between. */
    leftAxes: AxisSpec[];
    /** The axis whose scale is shown on the left; undefined only for a spec with no left axis. */
    activeLeftAxis: AxisSpec | undefined;
    setActiveLeftAxisId: (axisId: string) => void;
}

/**
 * Owns left y-axis selection for panels with multiple left axes.
 * Left-side axes are mutually exclusive on screen; this picks the visible one and
 * keeps the choice valid as the spec's axes change underneath it.
 */
export function useLeftYAxisSelection(panelId: string, axes: AxisSpec[]): LeftYAxisSelection {
    const leftAxes = useMemo(() => getLeftAxes(axes), [axes]);

    const preferredLeftAxisId = useDataViewerStore((state) => state.leftAxisSelections[panelId]);
    const setLeftAxisSelection = useDataViewerStore((state) => state.setLeftAxisSelection);

    const activeLeftAxis = useMemo(
        () => leftAxes.find((axis) => axis.id === preferredLeftAxisId) ?? leftAxes[0],
        [leftAxes, preferredLeftAxisId],
    );

    const setActiveLeftAxisId = useCallback(
        (axisId: string) => setLeftAxisSelection(panelId, axisId),
        [panelId, setLeftAxisSelection],
    );

    return { leftAxes, activeLeftAxis, setActiveLeftAxisId };
}
